import { faChevronRight } from "@fullscript/pro-regular-svg-icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Typography, Spacer, Button, FloatingLabelInput, useToast } from "@aviary";
import { LoginOptions } from "@shared/components/LoginOptions/LoginOptions";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";
import { gRecaptchaExecute } from "@shared/utils/gRecaptchaExecute/gRecaptchaExecute";
import { usePatientSignUpUnauthenticatedSharedMutation } from "@unauthenticated/shared/data/mutations/PatientSignUp.mutation.generated";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./JourneySignUpForm.styles";

// Form element ID constants
const FORM_IDS = {
  FIRST_NAME: "firstName",
  LAST_NAME: "lastName",
  EMAIL: "email",
  PASSWORD: "password",
} as const;

interface SignUpState {
  email: string;
  zipCode: string;
}

interface JourneySignUpFormProps {
  onSignUpSuccess?: () => void;
  email?: string;
  storeSlug?: string;
  storeId?: string;
  journeyPackageId?: string;
  signUpState?: SignUpState;
}

const JourneySignUpForm = ({
  onSignUpSuccess,
  email: initialEmail,
  storeSlug,
  storeId,
  journeyPackageId,
  signUpState,
}: JourneySignUpFormProps) => {
  const { t } = useTranslation();
  const { tablet } = useBreakpoints();
  const { makeToast } = useToast();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState(initialEmail || "");
  const [password, setPassword] = useState("");

  const isMobile = tablet.lessThan;

  // Build additional attributes for redirect after signup
  const additionalAttributes =
    storeSlug && journeyPackageId
      ? {
          redirectPath: (() => {
            const baseUrl = `/r/checkout/${storeSlug}/journey/${journeyPackageId}`;

            // Include signup state in redirect URL so it can be restored when user comes back
            if (signUpState) {
              const params = new URLSearchParams({
                email: signUpState.email,
                zipCode: signUpState.zipCode,
              });

              return `${baseUrl}?${params.toString()}`;
            }

            return baseUrl;
          })(),
        }
      : undefined;

  const [patientSignUp, { loading: isSignUpLoading }] =
    usePatientSignUpUnauthenticatedSharedMutation({
      onCompleted: data => {
        const signUpResult = data?.auth?.patientSignUp;

        if (signUpResult?.errors?.message) {
          makeToast("error", signUpResult.errors.message);

          return;
        }

        if (signUpResult?.errors?.fields) {
          const fieldErrors = signUpResult.errors.fields;
          if (fieldErrors.email) {
            makeToast("error", fieldErrors.email[0]);

            return;
          }
          if (fieldErrors.firstName) {
            makeToast("error", fieldErrors.firstName[0]);

            return;
          }
          if (fieldErrors.lastName) {
            makeToast("error", fieldErrors.lastName[0]);

            return;
          }
          if (fieldErrors.password) {
            makeToast("error", fieldErrors.password[0]);

            return;
          }
        }

        // Let backend handle the redirect
        if (signUpResult?.redirectPath) {
          window.location.assign(signUpResult.redirectPath);

          return;
        }

        // If no redirect path, call the success callback
        onSignUpSuccess?.();
      },
      onError: error => {
        makeToast("error", error?.message || t(l.journeyCheckout.signup.errors.signUpError));
      },
    });

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFirstName(e.target.value);
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLastName(e.target.value);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const handleCreateAccount = () => {
    if (!firstName || !lastName || !email || !password || !storeId) return;

    gRecaptchaExecute((captchaToken: string) => {
      patientSignUp({
        variables: {
          input: {
            attributes: {
              firstName,
              lastName,
              email,
              password,
              storeId,
              intake: false,
              optional: additionalAttributes,
            },
            captchaToken,
          },
        },
      });
    });
  };

  const renderSignUpHeader = () => (
    <div>
      <Typography type="h2">{t(l.journeyCheckout.signup.title)}</Typography>
      <Spacer height="half" />
      <Typography>{t(l.journeyCheckout.signup.subtitle)}</Typography>
      <Spacer height="one" />
    </div>
  );

  const renderSignUpForm = () => (
    <div>
      <div css={styles.nameRow}>
        <FloatingLabelInput
          id={FORM_IDS.FIRST_NAME}
          type="text"
          label={t(l.journeyCheckout.signup.firstNameLabel)}
          value={firstName}
          onChange={handleFirstNameChange}
          required
          css={styles.nameInput}
        />
        <FloatingLabelInput
          id={FORM_IDS.LAST_NAME}
          type="text"
          label={t(l.journeyCheckout.signup.lastNameLabel)}
          value={lastName}
          onChange={handleLastNameChange}
          required
          css={styles.nameInput}
        />
      </div>
      <Spacer height="one" />
      <FloatingLabelInput
        id={FORM_IDS.EMAIL}
        type="email"
        label={t(l.journeyCheckout.signup.emailLabel)}
        value={email}
        onChange={handleEmailChange}
        required
        disabled={!!initialEmail} // Disable if email was pre-filled
      />
      <Spacer height="one" />
      <FloatingLabelInput
        id={FORM_IDS.PASSWORD}
        type="password"
        label={t(l.journeyCheckout.signup.passwordLabel)}
        value={password}
        onChange={handlePasswordChange}
        required
      />
      <Spacer height="one" />
      <Button
        onClick={handleCreateAccount}
        isFullWidth
        size="large"
        isLoading={isSignUpLoading}
        disabled={!firstName || !lastName || !email || !password}
      >
        {t(l.journeyCheckout.signup.createAccountButton)}&nbsp;
        <FontAwesomeIcon icon={faChevronRight} />
      </Button>
    </div>
  );

  return (
    <div>
      {!!isMobile && <Spacer height="one" />}
      {renderSignUpHeader()}
      {renderSignUpForm()}
      <Spacer height="one" />
      <Typography type="footnote" isMarginless>
        {t(l.journeyCheckout.login.or)}
      </Typography>
      <Spacer height="one" />
      <LoginOptions
        commonSSOProps={{
          storeSlug,
          additionalAttributes,
        }}
        hideOrText
      />
    </div>
  );
};

export { JourneySignUpForm };
